import type { XhrStatus } from 'redux/reusable/xhrStatus';
import { createXhrStatusModule } from 'redux/reusable/xhrStatus';
import { actionCreatorFactory } from 'typescript-fsa';

const actionCreator = actionCreatorFactory('timely/activities');
export const { wrapFetching, wrapSaving, withXhrStatusReducer } =
  createXhrStatusModule('activities');

export enum RegularActivityTypes {
  create_client = 'create_client',
  archive_client = 'archive_client',
  create_project = 'create_project',
  invite_project = 'invite_project',
  logged_first_time = 'logged_first_time',
  invite_multiple_project = 'invite_multiple_project',
  archive_project = 'archive_project',
  project_budget_progress_fifty = 'project_budget_progress_fifty',
  project_budget_progress_eighty = 'project_budget_progress_eighty',
  project_budget_progress_hundred = 'project_budget_progress_hundred',
  daily_reminder = 'daily_reminder',
  delete_project = 'delete_project',
  project_budget_progress = 'project_budget_progress',
  create_report = 'create_report',
  shared_report = 'shared_report',
  unknown = 'unknown',
}

export enum AnomalyActivityTypes {
  user_logged_odd_duration = 'user_logged_odd_duration',
  user_logged_odd_time_from = 'user_logged_odd_time_from',
  user_logged_odd_time_to = 'user_logged_odd_time_to',
  user_logged_rare_project = 'user_logged_rare_project',
  user_logged_on_odd_day = 'user_logged_on_odd_day',
  user_logged_project_at_odd_interval = 'user_logged_project_at_odd_interval',
  user_logged_with_odd_logging_lag = 'user_logged_with_odd_logging_lag',
  user_logged_by_other_user = 'user_logged_by_other_user',
}

// Response type from backend api:

interface ClientRaw {
  active: boolean;
  external_id: string | null;
  id: number;
  name: string;
  color: string;
  updated_at: string;
}

interface EntityRaw {
  active: boolean;
  client: ClientRaw;
  color: string;
  id: number;
  name: string;
  updated_at: string;
}

interface ActivityUserRaw {
  avatar: {
    medium: string;
    medium_retina: string;
    timeline: string;
  };
  email: string;
  id: number;
  name: string;
  updated_at: string;
}

export interface ActivityRaw {
  activity_type: string;
  anomaly: boolean;
  notable?: boolean; // NOTE: this is currently not in the api, but will be in the future:
  associated_ids: Array<string>;
  detail: string;
  entity: EntityRaw;
  entity_deleted: boolean;
  entity_type: 'Project' | 'Company';
  id: number;
  message: string;
  parent_entity_name: string;
  updated_at: string;
  url: string;
  user: ActivityUserRaw;
}

export type ActivityNormalized = Omit<ActivityRaw, 'entity' | 'user'> & {
  user: number;
  entity: number;
};

export type ActivityUserNormalized = ActivityUserRaw;

export type ActivityClientNormalized = ClientRaw;

export interface ActivityProjectNormalized {
  id: number;
  name: string;
  color?: string;
  client: {
    id: number;
    name: string;
    color: string;
  };
  active: boolean;
  updated_at: string;
}

export interface ActivityQuery {
  limit?: number;
  offset?: number;
  order?: 'asc' | 'desc';
  project_id?: number;
  user_id?: number;
}

export interface ActivityEntities {
  projectActivities: {
    [id: number]: ActivityNormalized;
  };
  userActivities: {
    [id: number]: ActivityNormalized;
  };
  entityContext: {
    [id: number]: ActivityProjectNormalized | ActivityClientNormalized;
  };
  users: {
    [id: number]: ActivityUserNormalized;
  };
}

export interface ActivityResult {
  projectActivities: Array<number>;
  userActivities: Array<number>;
}

export interface State {
  entities: ActivityEntities;
  result: ActivityResult;
  xhrStatus: XhrStatus;
  maybeHasMore: boolean;
}

export type ActivityFetchType = 'projects' | 'users';

export interface FetchActivitiesArgument {
  filter: ActivityFetchType;
  query: ActivityQuery;
}

export const fetchedActivities = actionCreator<{
  result: Array<ActivityRaw>;
  params: FetchActivitiesArgument;
}>('FETCH_ACTIVITIES');
export const clearActivitiesCreator = actionCreator('CLEAR_ACTIVITIES');

export enum ActivityFilterType {
  All = 'All',
  Regular = 'Regular',
  Anomalies = 'Anomalies',
}

export const ActivityFilterTypeText: { [_key in ActivityFilterType]: string } = {
  [ActivityFilterType.All]: 'All activities',
  [ActivityFilterType.Anomalies]: 'Anomalies',
  [ActivityFilterType.Regular]: 'Regular',
};
